import React from 'react';
import { Flex, TextV2 } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import { useTranslation } from '@shared/core';
import { DiscountCouponBadge } from '@shared/components/DiscountCouponBadge';
import { useHotelDiscount } from '@shared/components/DiscountsProvider/hotels/HotelsDiscountsProvider';
import { ReservationCountBadge } from './ReservationCountBadge';
import type { HotelTileTagsData } from '../HotelTileCard.types';
import { AccommodationCombinedType } from '../HotelTileCard.types';

interface HotelTileTagsProps
  extends Readonly<{
    tags?: HotelTileTagsData;
  }> {}

export const HotelTileTags: React.FC<HotelTileTagsProps> = ({ tags }) => {
  const { tq } = useTranslation('hotelTile');
  const joyHotelDiscount = useHotelDiscount();

  if (!tags) return null;

  const { reservationCount, interestedGuestCount, showDiscountBadge, accommodationType } = tags;

  // Don't render anything if there are no tags to show
  const hasReservations = reservationCount && reservationCount > 0;
  const hasInterestedGuests = !reservationCount && interestedGuestCount && interestedGuestCount > 1;
  const hasDiscount = showDiscountBadge && joyHotelDiscount;

  if (!hasReservations && !hasInterestedGuests && !hasDiscount) {
    return null;
  }

  return (
    <>
      {/* Reservation count badge with tooltip */}
      {!!hasReservations && <ReservationCountBadge reservationCount={reservationCount} />}

      {/* Interested guests badge */}
      {hasInterestedGuests && !hasReservations && (
        <Flex
          borderRadius={pxToRem(20.632)}
          height={pxToRem(32)}
          alignItems="center"
          paddingX={pxToRem(4)}
          backdropFilter={`blur(${pxToRem(4)})`}
          backgroundColor="rgba(255, 255, 255, 0.6)"
        >
          <TextV2
            typographyVariant="label3"
            fontFamily="Inter UI"
            color="black"
            fontSize={pxToRem(13)}
            letterSpacing={pxToRem(-0.032)}
            fontWeight={600}
            alignSelf="center"
          >
            {tq`${interestedGuestCount}`}
          </TextV2>
        </Flex>
      )}

      {/* Discount badge */}
      {hasDiscount && (
        <DiscountCouponBadge
          discount={joyHotelDiscount}
          accommodationType={accommodationType as AccommodationCombinedType}
        />
      )}
    </>
  );
};
